// Kid Task View Component
// Simplified, kid-friendly task view for children ages 5-12
// Shows only tasks assigned to this family member
// Read-only except for marking tasks complete

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, Circle, LogOut, Sparkles, Star, Trophy,
  Calendar, AlertCircle
} from 'lucide-react';
import Confetti from 'react-confetti';
import toast from 'react-hot-toast';
import { familyPINService } from '../../api/familyPINService';
import { FamilyMemberSession } from '../../types/familyMember';
import { Task } from '../../types/api';

const KidTaskView: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [session, setSession] = useState<FamilyMemberSession | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  // Load session and tasks
  useEffect(() => {
    const sessionData = sessionStorage.getItem('family_member_session');
    if (!sessionData) {
      // No session, redirect to PIN login
      navigate('/family-login');
      return;
    }

    const parsedSession: FamilyMemberSession = JSON.parse(sessionData);
    setSession(parsedSession);
    loadTasks(parsedSession.member_id);
  }, [navigate]);

  const loadTasks = async (memberId: string) => {
    setLoading(true);
    try {
      const memberTasks = await familyPINService.getMemberTasks(memberId);
      setTasks(memberTasks);
    } catch (error) {
      console.error('[KidTaskView] Error loading tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    if (!session) return;

    setCompletingTaskId(taskId);

    try {
      await familyPINService.completeTask(taskId, session.member_id);

      // Update task in list
      setTasks(tasks.map(task =>
        task.id === taskId
          ? { ...task, status: 'completed', completed_at: new Date().toISOString() }
          : task
      ));

      // Show celebration
      setShowConfetti(true);
      toast.success('Great job! Task completed! 🎉', {
        icon: '🌟',
        duration: 4000
      });

      // Hide confetti after 3 seconds
      setTimeout(() => setShowConfetti(false), 3000);
    } catch (error) {
      console.error('[KidTaskView] Error completing task:', error);
      toast.error('Oops! Try again.');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleSignOut = () => {
    sessionStorage.removeItem('family_member_session');
    toast.success('Signed out successfully!');
    navigate('/family-login');
  };

  const getTaskIcon = (category: string) => {
    const icons: Record<string, string> = {
      household: '🧹',
      childcare: '👶',
      petcare: '🐾',
      errands: '🏃',
      homework: '📚',
      chores: '🧺',
      other: '✨'
    };
    return icons[category] || '✨';
  };

  const getFriendlyDate = (dateString: string | undefined) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset time for comparison
    const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowDate = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());

    if (taskDate.getTime() === todayDate.getTime()) {
      return 'Today';
    } else if (taskDate.getTime() === tomorrowDate.getTime()) {
      return 'Tomorrow';
    } else if (taskDate < todayDate) {
      return 'Past due';
    } else {
      const days = Math.ceil((taskDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
      return `In ${days} days`;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent':
        return 'from-red-400 to-orange-400';
      case 'high':
        return 'from-orange-400 to-yellow-400';
      case 'medium':
        return 'from-blue-400 to-purple-400';
      default:
        return 'from-green-400 to-emerald-400';
    }
  };

  // Calculate stats
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;
  const pendingCount = totalCount - completedCount;

  if (!session) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 pb-8">
      {/* Confetti Celebration */}
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-3xl shadow-lg"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                {session.member_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-bold">Hi, {session.member_name}!</h1>
                <p className="text-purple-100">Your Tasks</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold">Sign Out</span>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{totalCount}</div>
              <div className="text-sm text-purple-100">Total Tasks</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{completedCount}</div>
              <div className="text-sm text-purple-100">Completed</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{pendingCount}</div>
              <div className="text-sm text-purple-100">To Do</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="max-w-4xl mx-auto p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto" />
            <p className="text-gray-600 mt-4 text-lg">Loading your tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-12 text-center shadow-lg"
          >
            <Trophy className="w-20 h-20 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">All Done!</h2>
            <p className="text-xl text-gray-600">
              You don't have any tasks right now. Great job! 🎉
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task, index) => {
              const isCompleted = task.status === 'completed';
              const dueDate = getFriendlyDate(task.due_date);
              const isPastDue = dueDate === 'Past due';

              return (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                    isCompleted
                      ? 'border-green-300 bg-green-50/50'
                      : 'border-purple-200 hover:border-purple-400'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => !isCompleted && handleCompleteTask(task.id)}
                      disabled={isCompleted || completingTaskId === task.id}
                      className={`flex-shrink-0 transition-all ${
                        isCompleted
                          ? 'text-green-500 cursor-default'
                          : 'text-gray-300 hover:text-purple-500 hover:scale-110 cursor-pointer'
                      } ${completingTaskId === task.id ? 'animate-spin' : ''}`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-12 h-12" />
                      ) : (
                        <Circle className="w-12 h-12" />
                      )}
                    </button>

                    {/* Task Content */}
                    <div className="flex-1">
                      {/* Title and Icon */}
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-3xl">{getTaskIcon(task.category)}</span>
                        <h3 className={`text-2xl font-bold ${
                          isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'
                        }`}>
                          {task.title}
                        </h3>
                      </div>

                      {/* Description */}
                      {task.description && (
                        <p className={`text-lg mb-3 ${
                          isCompleted ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {task.description}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex flex-wrap gap-3">
                        {/* Due Date */}
                        {dueDate && (
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${
                            isPastDue
                              ? 'bg-red-100 text-red-700'
                              : dueDate === 'Today'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            <Calendar className="w-4 h-4" />
                            {dueDate}
                          </div>
                        )}

                        {/* Priority */}
                        {task.priority && task.priority !== 'low' && (
                          <div className={`px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${getPriorityColor(task.priority)} text-white`}>
                            {task.is_urgent ? '🔥 Urgent' : task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                          </div>
                        )}

                        {/* Completed Badge */}
                        {isCompleted && (
                          <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                            <Star className="w-4 h-4" />
                            Completed!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Encouragement Message */}
        {pendingCount > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg text-lg font-semibold">
              <Sparkles className="w-5 h-5" />
              {pendingCount === 1
                ? 'You can do it! Just 1 more task!'
                : `Keep going! ${pendingCount} tasks to complete!`}
            </div>
          </motion.div>
        )}

        {/* All Complete Celebration */}
        {pendingCount === 0 && totalCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-3xl p-8 text-center shadow-xl"
          >
            <Trophy className="w-16 h-16 mx-auto mb-4 text-white" />
            <h2 className="text-3xl font-bold text-white mb-2">
              Amazing Work, {session.member_name}!
            </h2>
            <p className="text-xl text-white/90">
              You completed all your tasks! You're a superstar! 🌟
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default KidTaskView;
